/**
 * Cities — the report's "By city" table, banded.
 *
 * Rows are emitted band by band: a `.is-total` band header, then that band's
 * members. The bands are the client's own fixed assignment from the plan file,
 * NOT a live ranking, because plan-versus-actual only lines up if the actuals
 * are grouped the way the plan was written — and where the band and the
 * period's ranking disagree the card says so rather than resorting itself.
 */
import type { ReactNode } from "react";
import { Delta, MetricCell, RowLabel, TableCard, fmt } from "../ds";
import { ErrorBox, State } from "../Panels";
import { useReport } from "../useReport";
import {
  type Basis,
  type DrillRow,
  type OpsWindow,
  DrillBody,
  FixedWindow,
  NotAvailable,
  bandColor,
  basisLabel,
  comparisonLabel,
  daysBetween,
  num,
  periodLabel,
  str,
  sumOf,
  useDrill,
} from "./opsLib";
import type { DateRange } from "@/lib/dateRange";

/** "Rest of India" always sorts last inside its band whatever its size — it is
 *  a residual, so ranking it against named cities would mean nothing. */
const RESIDUAL = "Rest of India";

type BandName = "Top-5" | "Next-5" | "Other";

export interface CityRow {
  name: string;
  gmv: number | null;
  prevGmv: number | null;
  units: number | null;
  drr: number | null;
  osa: number | null;
  listing: number | null;
  psl: number | null;
  /** True when the potential sales loss is the partner's measured figure. */
  pslMeasured: boolean;
  /** Which of the two crawl measurements this row's rupees came from. */
  crawlBasis: string;
  /** Fill rate for this city's warehouse group (Change 3). */
  fillRate: number | null;
  /** Tag shown when the city uses a proxy warehouse, e.g. "Fill rate from Delhi NCR warehouses". */
  fillRateTag: string | null;
}

/**
 * ONE PSL BASIS PER ROW, never a blend.
 *
 * The measured actual is what left the building and is the right answer for a
 * brand that is partner-aligned; the crawl estimate runs roughly 8x larger and
 * is only right for a competitor, where no actual exists. Adding rows of one
 * basis to rows of the other produces a number that is neither.
 */
function pslOf(r: Record<string, unknown>): {
  psl: number | null;
  measured: boolean;
} {
  const rowsActual = num(r["psl_rows_actual"]) ?? 0;
  const actual = num(r["sales_loss_actual"]);
  if (rowsActual > 0 && actual !== null) return { psl: actual, measured: true };
  return { psl: num(r["sales_loss_est"]), measured: false };
}

export function cityRowsOf(
  rows: Array<Record<string, unknown>>,
  basis: Basis,
): CityRow[] {
  return rows.map((r) => {
    const { psl, measured } = pslOf(r);
    return {
      name: str(r["city_name"]) || "—",
      // The only thing the basis toggle changes is GMV. MRP is what the plan
      // is written on; SP is what was realised after discount.
      gmv: basis === "mrp" ? num(r["gmv_mrp"]) : num(r["gmv"]),
      // Both bases now carry a previous-period total: the portal download is
      // read for the comparison window too, so an MRP delta is real rather
      // than absent.
      prevGmv: basis === "mrp" ? num(r["prev_gmv_mrp"]) : num(r["prev_gmv"]),
      units: num(r["units"]),
      drr: num(r["drr"]),
      // `osa`, not `osa_unweighted`: the server pools SUM(osa_numerator) /
      // SUM(city_live_stores) over the selected days and returns it under the
      // report's own name. The old key resolved to undefined and every OSA
      // cell would have read an em dash.
      osa: num(r["osa"]),
      listing: num(r["listing"]),
      psl,
      pslMeasured: measured,
      crawlBasis: str(r["crawl_basis"]),
      fillRate: num(r["fill_rate"]),
      fillRateTag:
        typeof r["fill_rate_tag"] === "string" ? r["fill_rate_tag"] : null,
    };
  });
}

const byGmvResidualLast = (a: CityRow, b: CityRow): number => {
  if (a.name === RESIDUAL) return 1;
  if (b.name === RESIDUAL) return -1;
  return (b.gmv ?? 0) - (a.gmv ?? 0);
};

interface Band {
  label: BandName;
  members: CityRow[];
  count: number;
  planned: number;
  missing: string[];
  gmv: number | null;
  units: number | null;
}

/**
 * Band cities by GMV rank — Top-5 = highest 5 by revenue, Next-5 = the next 5,
 * Other = the rest. "Rest of India" always lands in Other regardless of its GMV.
 *
 * Replaces the old hardcoded PLAN_BANDS which matched against a plan file's city
 * list and broke when names didn't match (e.g. "delhi ncr" vs "delhi").
 *
 * GMV and units are sums, so they pool here. OSA and listing % are RATIOS and
 * pooling them needs the numerator and denominator behind each city, which
 * this API does not return; averaging the percentages instead would weight a
 * four-store city the same as a four-hundred-store one. They are left blank on
 * band rows rather than computed wrongly.
 */
function bandsOf(rows: CityRow[]): Band[] {
  const ranked = rows
    .filter((r) => r.name !== RESIDUAL)
    .slice()
    .sort((a, b) => (b.gmv ?? 0) - (a.gmv ?? 0));
  const residual = rows.filter((r) => r.name === RESIDUAL);

  const top5 = ranked.slice(0, 5);
  const next5 = ranked.slice(5, 10);
  const other = [...ranked.slice(10), ...residual];

  const makeBand = (label: BandName, members: CityRow[]): Band => ({
    label,
    members,
    count: members.length,
    planned: members.length,
    missing: [],
    gmv: sumOf(members, (m) => m.gmv),
    units: sumOf(members, (m) => m.units),
  });

  return [
    ...(top5.length > 0 ? [makeBand("Top-5", top5)] : []),
    ...(next5.length > 0 ? [makeBand("Next-5", next5)] : []),
    ...(other.length > 0 ? [makeBand("Other", other)] : []),
  ];
}

/** The sales-loss cell: the value, coloured past the ₹1.5 L mark, over a
 *  caption that states which of the two bases it is on. */
function PslCell({ psl, measured }: { psl: number | null; measured: boolean }) {
  if (psl === null) return <NotAvailable />;
  return (
    <div className="metric">
      <div className="metric-figs">
        <div
          className="metric-val"
          style={{ color: psl > 150000 ? "var(--down)" : "var(--ink)" }}
        >
          {fmt(psl, "money")}
        </div>
      </div>
      {/*
        The published report tags this column "fixed window" because its source
        is a rolling 30-day snapshot that cannot be sliced. Here it is summed
        over the selected window like every other figure, so the caption states
        the BASIS instead of repeating a caveat that is not true of this number.
      */}
      <FixedWindow>{measured ? "measured" : "crawl estimate"}</FixedWindow>
    </div>
  );
}

export function CitiesCard({
  customer,
  range,
  basis,
}: {
  customer: string;
  range: DateRange;
  basis: Basis;
}) {
  const q = useReport("/v1/reports/ops/cities", customer, range);
  const { push } = useDrill();

  if (q.isLoading) return <State>Loading cities…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;

  const w: OpsWindow | undefined = q.data?.window;
  const scope = periodLabel(w?.start, w?.end);
  const raw = q.data?.rows ?? [];
  if (raw.length === 0) {
    return (
      <section className="card">
        <div className="card-head">
          <span className="card-title">By city</span>
        </div>
        <div className="empty">No crawl days in {scope}.</div>
      </section>
    );
  }

  const rows = cityRowsOf(raw, basis);
  const days = w ? daysBetween(w.start, w.end) : null;
  const totGmv = sumOf(rows, (r) => r.gmv) ?? 0;
  const bands = bandsOf(rows);

  const memberRow = (r: CityRow): DrillRow => ({
    key: r.name,
    // Every city has dark stores behind it and the API groups tf_store_metrics
    // by store_id for that city — same source as the MCP's resource=stores.
    cls: "clickable",
    onClick: () => {
      push(
        <CityStoreDrill
          customer={customer}
          range={range}
          city={r.name}
          cityFillRate={r.fillRate}
          cityFillRateTag={r.fillRateTag}
        />,
      );
    },
    cells: [
      <RowLabel
        key="c"
        name={r.name}
        sub={`${days === null ? "—" : `${days} day window`}${
          r.crawlBasis
            ? ` · crawl ${r.crawlBasis === "actual" ? "actuals" : "estimate"}`
            : ""
        }`}
      />,
      <MetricCell
        key="g"
        value={fmt(r.gmv, "money")}
        delta={<Delta cur={r.gmv} prev={r.prevGmv} good="up" />}
      />,
      fmt(r.units, "int"),
      fmt(r.drr, "int"),
      <MetricCell
        key="o"
        value={
          <span style={{ color: bandColor(r.osa) }}>{fmt(r.osa, "pct")}</span>
        }
      />,
      fmt(r.listing, "pct"),
      <PslCell key="p" psl={r.psl} measured={r.pslMeasured} />,
      r.fillRate !== null ? (
        <MetricCell
          key="fr"
          value={
            <span style={{ color: bandColor(r.fillRate) }}>
              {fmt(r.fillRate, "pct")}
            </span>
          }
          {...(r.fillRateTag
            ? { delta: <FixedWindow>{r.fillRateTag}</FixedWindow> }
            : {})}
        />
      ) : (
        "—"
      ),
    ],
  });

  const tableRows: DrillRow[] = bands.flatMap((b) => [
    {
      key: `band-${b.label}`,
      cls: "is-total",
      cells: [
        <RowLabel
          key="b"
          name={b.label}
          sub={`${b.count} cit${b.count === 1 ? "y" : "ies"} · ${totGmv ? (((b.gmv ?? 0) / totGmv) * 100).toFixed(1) : "—"}% of GMV`}
        />,
        <b key="g">{fmt(b.gmv, "money")}</b>,
        <b key="u">{fmt(b.units, "int")}</b>,
        // DRR is blank on a band row: a pooled run rate needs a day count
        // per member, not a sum of rates.
        "",
        <NotAvailable key="o">not poolable</NotAvailable>,
        <NotAvailable key="l">not poolable</NotAvailable>,
        "",
        "", // fill rate blank on band rows
      ],
    },
    ...b.members.map(memberRow),
  ]);

  const gmvTitle =
    basis === "mrp"
      ? "SUM(COALESCE(mrp_sales_actual, final_est_sales_mrp)) — dark-store crawl, at MRP"
      : "SUM(COALESCE(sp_sales_actual, final_est_sales_sp)) — dark-store crawl, selling price";

  return (
    <TableCard
      title="By city"
      scope={scope}
      asof={`Data to ${w?.as_of ?? "—"}`}
      cols={[
        { label: "City" },
        { label: `GMV (${basisLabel(basis)})`, type: "num", title: gmvTitle },
        {
          label: "Units",
          type: "num",
          title: "SUM(COALESCE(actual_units_sold, final_est_units))",
        },
        {
          label: "DRR (units/day)",
          type: "num",
          title: "SUM(units) / CALENDAR days in the window — not days in stock",
        },
        {
          label: "OSA % (unweighted)",
          type: "num",
          title:
            "SUM(osa_numerator) / SUM(osa_denominator), pooled over the window — never an average of daily percentages",
        },
        {
          label: "Listing %",
          type: "num",
          title:
            "SUM(listing_pct × listing_weight) / SUM(listing_weight) — weighted by the stores behind each listing",
        },
        {
          label: "Sales loss (est.)",
          type: "num",
          title:
            "Potential sales loss over the window: the partner's measured figure where it exists, the crawl estimate otherwise. The two are never added.",
        },
        {
          label: "Fill rate",
          type: "num",
          title:
            "SUM(delivered) / SUM(ordered) on CLOSED POs, pooled across the warehouse group serving this city",
        },
      ]}
      rows={tableRows}
      foot={
        <>
          <b>Ranked by GMV</b> &mdash; Top-5 and Next-5 are the highest-revenue
          cities in the selected window, not a fixed list. Each band row pools
          its members rather than averaging them.{" "}
          <b>Band rows carry pooled GMV and units only.</b> OSA and listing %
          are ratios: pooling them needs the numerator and denominator behind
          each city, which this API does not return, and averaging the
          percentages would weight a four-store city like a four-hundred-store
          one. They are left blank rather than computed wrongly. All figures are{" "}
          <b>pooled for the selected window</b> &mdash; OSA is{" "}
          <code>SUM(osa_numerator)/SUM(osa_denominator)</code> over those days,
          never an average of daily percentages, and pooling is what absorbs a
          change in crawl coverage correctly.{" "}
          <b>GMV here is the dark-store crawl</b>, a narrower footprint than the
          portal total on the control tower: the two are not meant to add up.{" "}
          {basis === "mrp" && (
            <>
              The comparison is blank on the MRP basis because the API returns a
              previous-period total on the selling-price basis only &mdash; a
              delta between an MRP figure and an SP one would be measuring a
              discount, not growth.{" "}
            </>
          )}
          <b>Sales loss states its own basis per row:</b> measured where the
          partner reported it, the crawl estimate otherwise. The estimate runs
          about eight times the measured figure, so the column is never summed
          across the two. <b>Click a city for store-level OSA within it.</b>
        </>
      }
    />
  );
}

/** Store-level OSA for a city — same source as MCP resource=stores (tf_store_metrics). */
function CityStoreDrill({
  customer,
  range,
  city,
  cityFillRate,
  cityFillRateTag,
}: {
  customer: string;
  range: DateRange;
  city: string;
  cityFillRate?: number | null;
  cityFillRateTag?: string | null;
}) {
  const q = useReport("/v1/reports/ops/city-stores", customer, range, { city });
  const w: OpsWindow | undefined = q.data?.window;
  const raw: Array<Record<string, unknown>> = q.data?.rows ?? [];

  const stores = raw.map((r) => ({
    storeId: str(r["store_id"]),
    locality: str(r["locality"]) || "—",
    osa: num(r["osa_pct"]),
    soh: num(r["soh"]),
    units: num(r["units"]),
    drr: num(r["drr"]),
    doi: num(r["doi"]),
    psl: num(r["psl"]),
    skus: num(r["skus"]),
  }));

  const totalPsl = sumOf(stores, (s) => s.psl) ?? 0;
  const totalUnits = sumOf(stores, (s) => s.units) ?? 0;

  const cells = (s: (typeof stores)[number]): ReactNode[] => [
    <RowLabel key="s" name={s.locality} sub={`store ${s.storeId}`} />,
    <span key="o" style={{ color: bandColor(s.osa), fontWeight: 600 }}>
      {fmt(s.osa, "pct")}
    </span>,
    fmt(s.skus, "int"),
    fmt(s.soh, "int"),
    fmt(s.units, "int"),
    fmt(s.drr, "int"),
    fmt(s.doi, "int"),
    <span
      key="p"
      style={{ color: (s.psl ?? 0) > 5000 ? "var(--down)" : "var(--ink)" }}
    >
      {fmt(s.psl, "money")}
    </span>,
  ];

  return (
    <DrillBody
      title={`Stores — ${city}`}
      count={`${stores.length} store${stores.length === 1 ? "" : "s"} · ${periodLabel(w?.start, w?.end)}${cityFillRate !== null && cityFillRate !== undefined ? ` · Fill rate ${cityFillRate.toFixed(1)}%` : ""}${cityFillRateTag ? ` (${cityFillRateTag})` : ""}`}
      loading={q.isLoading}
      error={q.isError ? q.error.message : null}
      empty={
        stores.length === 0
          ? `No store data for ${city} in ${periodLabel(w?.start, w?.end)}.`
          : null
      }
      cols={[
        { label: "Store" },
        {
          label: "OSA %",
          type: "num",
          title:
            "Pooled OSA across all SKUs at this store — SUM(osa_numerator) / SUM(days_observed), same as MCP resource=stores",
        },
        { label: "SKUs", type: "num" },
        {
          label: "SOH",
          type: "num",
          title: "Stock on hand — latest inventory units",
        },
        {
          label: "Units",
          type: "num",
          title: "Estimated units sold in the window",
        },
        { label: "DRR", type: "num", title: "Daily run rate — units per day" },
        {
          label: "DOI",
          type: "num",
          title: "Days of inventory — stock on hand / DRR",
        },
        {
          label: "Sales loss",
          type: "num",
          title: "Estimated potential sales loss (crawl)",
        },
      ]}
      rows={[
        ...stores.map((s) => ({ key: s.storeId, cells: cells(s) })),
        ...(stores.length > 0
          ? [
              {
                key: "__total",
                cls: "is-total",
                cells: [
                  <b key="t">
                    {stores.length} store{stores.length === 1 ? "" : "s"}
                  </b>,
                  <NotAvailable key="o">not poolable</NotAvailable>,
                  "",
                  <b key="h">
                    {fmt(
                      sumOf(stores, (s) => s.soh),
                      "int",
                    )}
                  </b>,
                  <b key="u">{fmt(totalUnits, "int")}</b>,
                  "",
                  "",
                  <b key="p">{fmt(totalPsl, "money")}</b>,
                ] as ReactNode[],
              },
            ]
          : []),
      ]}
      foot={
        <>
          <span>
            <b>Store-level OSA</b> for {city}, from the same source as the
            MCP&rsquo;s <code>resource=stores</code> (
            <code>tf_store_metrics</code>). Each row groups all the
            brand&rsquo;s SKUs at that store. OSA is pooled, never averaged.
            Sorted by potential sales loss &mdash; the stores losing the most
            revenue appear first.
          </span>
          <span className="spacer" />
          <span>Data to {w?.as_of ?? "—"}</span>
        </>
      }
    />
  );
}

/** Cities filtered to one sub-line — the second level of the sub-line drill. */
export function SubLineCityDrill({
  customer,
  range,
  basis,
  subLine,
}: {
  customer: string;
  range: DateRange;
  basis: Basis;
  subLine: string;
}) {
  // sub_line, NOT category: a plan line ("Core 500ml") matches no
  // l2_category_name, so sending it as `category` filtered every row away and
  // the drill opened empty.
  const q = useReport("/v1/reports/ops/cities", customer, range, {
    sub_line: subLine,
  });
  const w: OpsWindow | undefined = q.data?.window;
  const rows = cityRowsOf(q.data?.rows ?? [], basis).sort(byGmvResidualLast);
  const tot = sumOf(rows, (r) => r.gmv) ?? 0;
  const worst = rows
    .filter((r) => r.osa !== null)
    .sort((a, b) => (a.osa ?? 0) - (b.osa ?? 0))[0];

  const cells = (r: CityRow): ReactNode[] => [
    <RowLabel
      key="c"
      name={r.name}
      sub={
        r.crawlBasis
          ? `crawl ${r.crawlBasis === "actual" ? "actuals" : "estimate"}`
          : ""
      }
    />,
    fmt(r.gmv, "money"),
    <Delta key="d" cur={r.gmv} prev={r.prevGmv} good="up" />,
    fmt(r.units, "int"),
    <span key="o" style={{ color: bandColor(r.osa) }}>
      {fmt(r.osa, "pct")}
    </span>,
    fmt(r.listing, "pct"),
    <span key="s" style={{ color: "var(--ink-2)" }}>
      {tot ? `${(((r.gmv ?? 0) / tot) * 100).toFixed(1)}%` : "—"}
    </span>,
    r.fillRate !== null ? (
      <span key="fr" style={{ color: bandColor(r.fillRate) }}>
        {fmt(r.fillRate, "pct")}
      </span>
    ) : (
      "—"
    ),
  ];

  return (
    <DrillBody
      title={`${subLine} — by city`}
      count={`${rows.length} cit${rows.length === 1 ? "y" : "ies"} · ${periodLabel(w?.start, w?.end)}`}
      loading={q.isLoading}
      error={q.isError ? q.error.message : null}
      empty={
        rows.length === 0
          ? `No city carries ${subLine} in ${periodLabel(w?.start, w?.end)}.`
          : null
      }
      cols={[
        { label: "City" },
        { label: `GMV (${basisLabel(basis)})`, type: "num" },
        { label: `vs ${comparisonLabel(w)}`, type: "num" },
        { label: "Units", type: "num" },
        { label: "OSA %", type: "num" },
        { label: "Listing %", type: "num" },
        { label: "Share of sub-line", type: "num" },
        { label: "Fill rate", type: "num" },
      ]}
      rows={[
        ...rows.map((r) => ({ key: r.name, cells: cells(r) })),
        ...(rows.length > 0
          ? [
              {
                key: "__total",
                cls: "is-total",
                cells: [
                  <b key="t">{subLine}</b>,
                  <b key="g">{fmt(tot, "money")}</b>,
                  "",
                  <b key="u">
                    {fmt(
                      sumOf(rows, (r) => r.units),
                      "int",
                    )}
                  </b>,
                  <NotAvailable key="o">not poolable</NotAvailable>,
                  <NotAvailable key="l">not poolable</NotAvailable>,
                  <b key="s">100.0%</b>,
                  "", // fill rate blank on total
                ] as ReactNode[],
              },
            ]
          : []),
      ]}
      foot={
        <>
          <span>
            <b>This level follows the date picker.</b> Cities are filtered to{" "}
            <code>l2_category_name = &ldquo;{subLine}&rdquo;</code>, so the
            rupees are the dark-store crawl at the same grain as the Cities
            page, and OSA is pooled &mdash;{" "}
            <code>SUM(osa_numerator)/SUM(osa_denominator)</code>, never an
            average.{" "}
            {worst && (
              <>
                Weakest shelf: <b>{worst.name}</b> at {fmt(worst.osa, "pct")}.
              </>
            )}
          </span>
          <span className="spacer" />
          <span>Data to {w?.as_of ?? "—"}</span>
        </>
      }
    />
  );
}
